import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Hub } from 'aws-amplify/utils';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell, sidebarSessionKey } from './components/layout/AppShell';
import { AccountPage } from './pages/AccountPage';
import { DashboardPage } from './pages/DashboardPage';
import { SalesPage } from './pages/SalesPage';
import { UploadPage } from './pages/UploadPage';
import { TargetsPage } from './pages/TargetsPage';
import { StoresPage } from './pages/StoresPage';
import { FoodPage } from './pages/FoodPage';
import { LaborPage } from './pages/LaborPage';
import { FixedCostPage } from './pages/FixedCostPage';
import { LoanPage } from './pages/LoanPage';
import { LoginPage } from './pages/LoginPage';
import { FreeeSalesPage } from './pages/FreeeSalesPage';
import { DrinkAnalysisPage } from './pages/DrinkAnalysisPage';
import { WeeklySalesPage } from './pages/WeeklySalesPage';
import { YearOverYearPage } from './pages/YearOverYearPage';
import { AnnualSalesPage, MonthlySalesPage, YearlySalesPage } from './pages/SalesPeriodPages';
import { StayOccupancyPage } from './pages/StayOccupancyPage';
import { OccupancyHeatmapPage } from './pages/OccupancyHeatmapPage';
import { ProfitSimulatorPage } from './pages/ProfitSimulatorPage';
import { CalendarEventsPage } from './pages/CalendarEventsPage';
import { InventoryPage } from './pages/InventoryPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { Version } from './Objects/Version';
import { ME_QUERY } from './graphql/queries';
import { gql } from './lib/api';
import { consumeReturnTo, getUserEmail, isAuthenticated, logout } from './lib/auth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 60_000,
    },
  },
});

type AuthState = 'loading' | 'authenticated' | 'unauthenticated' | 'forbidden';

interface CurrentUser {
  email: string;
  role: 'viewer' | 'admin';
}

const App = () => {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<CurrentUser['role']>('viewer');

  const refreshAuth = async () => {
    const authenticated = await isAuthenticated();
    if (!authenticated) {
      if (window.location.pathname !== '/auth/callback') setAuthState('unauthenticated');
      return;
    }

    try {
      const { me } = await gql<{ me: CurrentUser }>(ME_QUERY);
      setUserEmail(me.email);
      setUserRole(me.role);
      setAuthState('authenticated');
      if (window.location.pathname === '/auth/callback') {
        window.history.replaceState({}, '', consumeReturnTo());
      }
    } catch {
      setUserEmail(await getUserEmail());
      setAuthState('forbidden');
    }
  };

  useEffect(() => {
    const cancel = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect_failure') {
        setAuthState('unauthenticated');
        return;
      }
      if (
        payload.event === 'signInWithRedirect' ||
        payload.event === 'signedIn' ||
        payload.event === 'customOAuthState'
      ) {
        void refreshAuth();
      }
    });
    void refreshAuth();
    return cancel;
  }, []);

  const signOutAndReset = async (selectAccountOnNextLogin = false) => {
    sessionStorage.removeItem(sidebarSessionKey(userEmail));
    queryClient.clear();
    await logout({ selectAccountOnNextLogin });
    setUserEmail('');
    setUserRole('viewer');
    setAuthState('unauthenticated');
  };

  const handleSignOut = () => signOutAndReset();
  const handleAccountSwitch = () => signOutAndReset(true);

  if (authState === 'loading') {
    return <LoadingScreen />;
  }

  if (authState === 'forbidden') {
    return <ForbiddenScreen userEmail={userEmail} onSwitchAccount={handleAccountSwitch} />;
  }

  return (
    <BrowserRouter>
      {authState === 'authenticated' ? (
        <AuthenticatedRoutes userEmail={userEmail} userRole={userRole} onSignOut={handleSignOut} />
      ) : (
        <UnauthenticatedRoutes />
      )}
    </BrowserRouter>
  );
};

interface AuthenticatedRoutesProps {
  userEmail: string;
  userRole: 'viewer' | 'admin';
  onSignOut: () => void;
}

const AuthenticatedRoutes = ({ userEmail, userRole, onSignOut }: AuthenticatedRoutesProps) => (
  <QueryClientProvider client={queryClient}>
    <AppShell userEmail={userEmail} userRole={userRole} onSignOut={onSignOut}>
      <Routes>
        <Route path="/" element={<DashboardPage isAdmin={userRole === 'admin'} />} />
        <Route path="/sales" element={<SalesPage isAdmin={userRole === 'admin'} />} />
        <Route path="/food" element={<FoodPage />} />
        <Route path="/labor" element={<AdminRoute role={userRole}><LaborPage /></AdminRoute>} />
        <Route path="/fixed-costs" element={<FixedCostPage />} />
        {/* 家賃は店舗マスタの monthlyRent 固定値を参照する方針に変更（旧 /rent は店舗マスタへ誘導） */}
        <Route path="/rent" element={<Navigate to="/settings/stores" replace />} />
        <Route path="/loans" element={<AdminRoute role={userRole}><LoanPage /></AdminRoute>} />
        <Route path="/settings/upload" element={<UploadPage />} />
        <Route path="/settings/targets" element={<TargetsPage />} />
        <Route path="/settings/stores" element={<StoresPage />} />
        <Route path="/sales/daily" element={<FreeeSalesPage isAdmin={userRole === 'admin'} />} />
        <Route path="/kpi/drinks" element={<DrinkAnalysisPage />} />
        <Route path="/kpi/weekly-sales" element={<WeeklySalesPage />} />
        <Route path="/kpi/year-over-year" element={<YearOverYearPage />} />
        <Route path="/kpi/monthly-sales" element={<MonthlySalesPage />} />
        <Route path="/kpi/annual-sales" element={<AnnualSalesPage />} />
        <Route path="/kpi/yearly-sales" element={<YearlySalesPage />} />
        <Route path="/kpi/stay-occupancy" element={<StayOccupancyPage />} />
        <Route path="/kpi/occupancy-heatmap" element={<OccupancyHeatmapPage />} />
        <Route path="/kpi/profit-simulator" element={<ProfitSimulatorPage />} />
        <Route path="/calendar/events" element={<CalendarEventsPage isAdmin={userRole === 'admin'} />} />
        <Route path="/inventory" element={<InventoryPage isAdmin={userRole === 'admin'} />} />
        <Route path="/documents" element={<AdminRoute role={userRole}><DocumentsPage /></AdminRoute>} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/version" element={<Version />} />

        {/* 旧 URL からのリダイレクト（既存ブックマークの互換性確保） */}
        <Route path="/products" element={<Navigate to="/sales" replace />} />
        <Route path="/costs" element={<Navigate to="/food" replace />} />
        <Route path="/staff" element={<Navigate to="/labor" replace />} />
        <Route path="/upload" element={<Navigate to="/settings/upload" replace />} />
        <Route path="/targets" element={<Navigate to="/settings/targets" replace />} />
        <Route path="/stores" element={<Navigate to="/settings/stores" replace />} />
        <Route path="/settings" element={<Navigate to="/settings/upload" replace />} />
        <Route path="/settings/freee-sales" element={<Navigate to="/sales/daily" replace />} />

        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  </QueryClientProvider>
);

const AdminRoute = ({ role, children }: { role: CurrentUser['role']; children: React.ReactNode }) =>
  role === 'admin' ? <>{children}</> : <AdminOnlyScreen />;

const AdminOnlyScreen = () => (
  <section style={forbiddenStyles.panel}>
    <h1 style={forbiddenStyles.title}>管理者専用ページです</h1>
    <p style={forbiddenStyles.message}>このページの閲覧には管理者権限が必要です。</p>
  </section>
);

const UnauthenticatedRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="*" element={<RedirectToLogin />} />
  </Routes>
);

const RedirectToLogin = () => {
  const location = useLocation();
  const from = `${location.pathname}${location.search}`;
  return <Navigate to="/login" replace state={{ from }} />;
};

const LoadingScreen = () => (
  <div style={loadingStyles.page}>
    <div style={loadingStyles.panel}>読み込み中...</div>
  </div>
);

const ForbiddenScreen = ({ userEmail, onSwitchAccount }: { userEmail: string; onSwitchAccount: () => void }) => (
  <div style={loadingStyles.page}>
    <div style={forbiddenStyles.panel}>
      <h1 style={forbiddenStyles.title}>このダッシュボードを利用できません</h1>
      <p style={forbiddenStyles.message}>
        確認済みメールアドレスが loop2014.co.jp のアカウントでログインしてください。
      </p>
      {userEmail && <p style={forbiddenStyles.email}>ログイン中: {userEmail}</p>}
      <button type="button" onClick={onSwitchAccount} style={forbiddenStyles.button}>
        別のアカウントでログイン
      </button>
    </div>
  </div>
);

const loadingStyles = {
  page: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: '#f3f2f2',
    color: '#495057',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Hiragino Sans", "Yu Gothic UI", "Meiryo UI", sans-serif',
  },
  panel: {
    padding: '1rem 1.25rem',
    background: '#fff',
    border: '1px solid #d4d1cf',
    borderRadius: 8,
  },
} as const satisfies Record<string, React.CSSProperties>;

const forbiddenStyles = {
  panel: {
    width: 'min(28rem, calc(100% - 3rem))',
    padding: '2rem',
    background: '#fff',
    border: '1px solid #ffc9c9',
    borderRadius: 8,
  },
  title: { margin: 0, fontSize: '1.3rem', color: '#aa0b56' },
  message: { margin: '1rem 0 0', lineHeight: 1.7 },
  email: { margin: '0.75rem 0 0', color: '#6c757d', fontSize: '0.9rem' },
  button: {
    marginTop: '1.25rem',
    padding: '0.65rem 0.9rem',
    border: 0,
    borderRadius: 6,
    background: '#495057',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
  },
} as const satisfies Record<string, React.CSSProperties>;

export default App;
